/**
 * Notification Bell Component
 *
 * Header icon with unread badge + dropdown panel.
 * Polls unread count every 60s. Click opens 5 most recent notifications.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useNotifications } from '../hooks/useNotifications';
import { timeAgo } from '../lib/timeAgo';
import { BellIcon } from './shared/icons';
import { LoadingSpinner } from './shared/LoadingSpinner';

import type { CustomerNotification } from '../services/customerNotificationService';

function priorityDotColor(priority: string): string {
  switch (priority) {
    case 'CRITICAL':
      return 'hsl(var(--color-error-500))';
    case 'HIGH':
      return 'hsl(var(--color-warning-500))';
    case 'LOW':
      return 'hsl(var(--color-neutral-400))';
    default:
      return 'hsl(var(--color-primary-500))';
  }
}

/**
 *
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const {
    unreadCount,
    notifications,
    loading,
    markAsRead,
    markAllAsRead,
    fetchNotifications,
  } = useNotifications();

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (open) {
      void fetchNotifications(5);
    }
  }, [open, fetchNotifications]);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const handleNotificationClick = (notification: CustomerNotification) => {
    if (!notification.read) {
      void markAsRead(notification.notificationId);
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
    setOpen(false);
  };

  const handleMarkAllRead = () => {
    void markAllAsRead();
  };

  const badgeText = unreadCount > 9 ? '9+' : String(unreadCount);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="relative rounded-md p-2 transition-colors hover:bg-neutral-50"
        style={{ color: 'hsl(var(--color-neutral-500))' }}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <BellIcon className="h-5 w-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white"
            style={{ backgroundColor: 'hsl(var(--color-error-500))' }}
          >
            {badgeText}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-1 w-80 origin-top-right rounded-md border bg-surface-elevated shadow-lg"
          style={{
            borderColor: 'hsl(var(--color-neutral-200))',
            animation: 'fadeSlideDown 150ms ease-out',
            zIndex: 50,
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: 'hsl(var(--color-neutral-100))' }}>
            <span className="text-sm font-semibold" style={{ color: 'hsl(var(--color-neutral-900))' }}>
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs font-medium"
                style={{ color: 'hsl(var(--color-primary-600))' }}
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-80 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner size="sm" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <BellIcon className="mx-auto h-8 w-8" style={{ color: 'hsl(var(--color-neutral-300))' }} />
                <p className="mt-2 text-sm" style={{ color: 'hsl(var(--color-neutral-500))' }}>
                  No notifications yet
                </p>
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.notificationId}
                  onClick={() => handleNotificationClick(n)}
                  className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-neutral-50"
                  style={{
                    backgroundColor: n.read ? undefined : 'hsl(var(--color-primary-50) / 0.3)',
                  }}
                >
                  <span
                    className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: priorityDotColor(n.priority) }}
                  />
                  <div className="min-w-0 flex-1">
                    <p
                      className="truncate text-sm"
                      style={{
                        color: 'hsl(var(--color-neutral-900))',
                        fontWeight: n.read ? 400 : 600,
                      }}
                    >
                      {n.title}
                    </p>
                    <p
                      className="mt-0.5 truncate text-xs"
                      style={{ color: 'hsl(var(--color-neutral-500))' }}
                    >
                      {n.body}
                    </p>
                    <p
                      className="mt-1 text-xs"
                      style={{ color: 'hsl(var(--color-neutral-400))' }}
                    >
                      {timeAgo(n.createdAt)}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="border-t px-4 py-2" style={{ borderColor: 'hsl(var(--color-neutral-100))' }}>
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="block text-center text-xs font-medium"
              style={{ color: 'hsl(var(--color-primary-600))' }}
            >
              View all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
